import type { SudokuGrid, CellValue } from '../../../../shared/types';
import { cloneGrid } from './utils';

/**
 * Sudoku Solver
 * Solves Sudoku puzzles using backtracking algorithm
 */
export class SudokuSolver {
  private readonly SIZE = 9;
  private readonly BOX_SIZE = 3;

  /**
   * Solve a Sudoku puzzle
   * Returns the solved grid or null if no solution exists
   */
  solve(grid: SudokuGrid): SudokuGrid | null {
    const workingGrid = cloneGrid(grid);
    
    if (this.solveBacktrack(workingGrid)) {
      return workingGrid;
    }
    
    return null;
  }

  /**
   * Backtracking algorithm to solve Sudoku
   */
  private solveBacktrack(grid: SudokuGrid): boolean {
    const emptyCell = this.findEmptyCell(grid);
    
    if (!emptyCell) {
      return true; // Solved!
    }

    const { row, col } = emptyCell;

    for (let num = 1; num <= 9; num++) {
      if (this.isValid(grid, row, col, num as CellValue)) {
        grid[row][col] = num as CellValue;

        if (this.solveBacktrack(grid)) {
          return true;
        }

        grid[row][col] = 0; // Backtrack
      }
    }

    return false;
  }

  /**
   * Find the first empty cell in the grid
   */
  private findEmptyCell(grid: SudokuGrid): { row: number; col: number } | null {
    for (let row = 0; row < this.SIZE; row++) {
      for (let col = 0; col < this.SIZE; col++) {
        if (grid[row][col] === 0) {
          return { row, col };
        }
      }
    }
    return null;
  }

  /**
   * Check if placing a number at a position is valid
   */
  private isValid(grid: SudokuGrid, row: number, col: number, num: CellValue): boolean {
    // Check row
    for (let x = 0; x < this.SIZE; x++) {
      if (grid[row][x] === num) {
        return false;
      }
    }

    // Check column
    for (let x = 0; x < this.SIZE; x++) {
      if (grid[x][col] === num) {
        return false;
      }
    }

    // Check 3x3 box
    const boxRow = Math.floor(row / this.BOX_SIZE) * this.BOX_SIZE;
    const boxCol = Math.floor(col / this.BOX_SIZE) * this.BOX_SIZE;

    for (let i = 0; i < this.BOX_SIZE; i++) {
      for (let j = 0; j < this.BOX_SIZE; j++) {
        if (grid[boxRow + i][boxCol + j] === num) {
          return false;
        }
      }
    }

    return true;
  }

  /**
   * Get all possible candidates for a cell
   */
  getCandidates(grid: SudokuGrid, row: number, col: number): CellValue[] {
    if (grid[row][col] !== 0) {
      return [];
    }

    const candidates: CellValue[] = [];

    for (let num = 1; num <= 9; num++) {
      if (this.isValid(grid, row, col, num as CellValue)) {
        candidates.push(num as CellValue);
      }
    }

    return candidates;
  }

  /**
   * Find a cell with the fewest candidates (for optimization)
   */
  findBestCell(grid: SudokuGrid): { row: number; col: number; candidates: CellValue[] } | null {
    let bestCell: { row: number; col: number; candidates: CellValue[] } | null = null;
    let minCandidates = 10;

    for (let row = 0; row < this.SIZE; row++) {
      for (let col = 0; col < this.SIZE; col++) {
        if (grid[row][col] === 0) {
          const candidates = this.getCandidates(grid, row, col);
          
          if (candidates.length === 0) {
            return null; // No solution possible
          }
          
          if (candidates.length < minCandidates) {
            minCandidates = candidates.length;
            bestCell = { row, col, candidates };
            
            if (minCandidates === 1) {
              return bestCell; // Can't get better than 1 candidate
            }
          }
        }
      }
    }

    return bestCell;
  }

  /**
   * Get a hint for the next move
   * Returns the easiest cell to fill (with fewest candidates)
   */
  getHint(grid: SudokuGrid): { row: number; col: number; value: CellValue } | null {
    const bestCell = this.findBestCell(grid);
    
    if (!bestCell || bestCell.candidates.length === 0) {
      return null;
    }

    return {
      row: bestCell.row,
      col: bestCell.col,
      value: bestCell.candidates[0],
    };
  }

  /**
   * Check if the current state has any conflicts
   */
  findConflicts(grid: SudokuGrid): Array<{ row: number; col: number }> {
    const conflicts: Array<{ row: number; col: number }> = [];

    for (let row = 0; row < this.SIZE; row++) {
      for (let col = 0; col < this.SIZE; col++) {
        const value = grid[row][col];
        
        if (value === 0) continue;

        // Temporarily remove the value to check
        grid[row][col] = 0;
        
        if (!this.isValid(grid, row, col, value)) {
          conflicts.push({ row, col });
        }
        
        // Restore the value
        grid[row][col] = value;
      }
    }

    return conflicts;
  }

  /**
   * Validate if the grid follows Sudoku rules (no duplicates in rows/cols/boxes)
   */
  validate(grid: SudokuGrid): boolean {
    return this.findConflicts(grid).length === 0;
  }
}


